import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  ShieldCheck,
  Loader2,
  RotateCw,
  XCircle,
  CheckCircle2,
  AlertCircle,
  Calendar,
  FileText,
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const getPrincipalId = () => localStorage.getItem('sipl_principal_id') || '';

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const statusVariant = (status) => {
  switch (status) {
    case 'active':
      return { bg: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle2, label: 'Active' };
    case 'withdrawn':
      return { bg: 'bg-red-100 text-red-700 border-red-200', icon: XCircle, label: 'Withdrawn' };
    case 'expired':
      return { bg: 'bg-amber-100 text-amber-700 border-amber-200', icon: AlertCircle, label: 'Expired' };
    default:
      return { bg: 'bg-slate-100 text-slate-700 border-slate-200', icon: FileText, label: status || 'Unknown' };
  }
};

const ConsentManager = ({ open, onOpenChange }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [principalId, setPrincipalId] = useState('');
  const [actionOn, setActionOn] = useState(null); // consent_id currently being acted on
  const [noPrincipal, setNoPrincipalId] = useState(false);

  const fetchStatus = useCallback(async (pid) => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API}/api/consent/status?principal_id=${encodeURIComponent(pid)}`
      );
      const json = await res.json();
      const list = json?.data?.items || [];
      setItems(list);
    } catch (err) {
      console.error(err);
      toast({
        title: 'Could not load consents',
        description: 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (!open) return;
    const pid = getPrincipalId();
    if (!pid) {
      setNoPrincipalId(true);
      setItems([]);
      return;
    }
    setNoPrincipalId(false);
    setPrincipalId(pid);
    fetchStatus(pid);
  }, [open, fetchStatus]);

  const handleWithdraw = async (consentId) => {
    setActionOn(consentId);
    try {
      const res = await fetch(`${API}/api/consent/withdraw`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ consent_id: consentId, reason: 'user_requested_via_manager' }),
      });
      const json = await res.json();
      if (json?.success) {
        toast({
          title: 'Consent withdrawn',
          description: 'Your consent has been revoked and audit-logged.',
        });
        // Clear local consent marker so banner reappears next visit
        localStorage.removeItem('sipl_dpdp_consent_v1');
        await fetchStatus(principalId);
      } else {
        toast({
          title: 'Withdraw failed',
          description: json?.data?.detail || 'Please try again.',
          variant: 'destructive',
        });
      }
    } finally {
      setActionOn(null);
    }
  };

  const handleRenew = async (consentId) => {
    setActionOn(consentId);
    try {
      const res = await fetch(`${API}/api/consent/renew`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ consent_id: consentId }),
      });
      const json = await res.json();
      if (json?.success) {
        toast({
          title: 'Consent renewed',
          description: 'A fresh consent record has been created.',
        });
        await fetchStatus(principalId);
      } else {
        toast({
          title: 'Renew failed',
          description: json?.data?.detail || 'Please try again.',
          variant: 'destructive',
        });
      }
    } finally {
      setActionOn(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="consent-manager-modal"
        className="max-w-2xl max-h-[85vh] overflow-y-auto"
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-cyan-50 border border-cyan-200 flex items-center justify-center">
              <ShieldCheck className="h-5 w-5 text-cyan-600" />
            </div>
            <div>
              <DialogTitle className="text-xl">Manage My Consent</DialogTitle>
              <DialogDescription className="text-sm">
                DPDPA §6 — view, withdraw or renew consents you&apos;ve granted to Sanghvi Infotech.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {noPrincipal ? (
          <div className="py-8 text-center">
            <AlertCircle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
            <p className="text-slate-700 font-medium mb-1">No consent history found</p>
            <p className="text-sm text-slate-500">
              You haven&apos;t granted any consent on this device yet. Accept the DPDPA notice
              when it appears to record your first consent.
            </p>
          </div>
        ) : loading ? (
          <div className="py-12 flex items-center justify-center">
            <Loader2 className="h-6 w-6 text-cyan-600 animate-spin" />
            <span className="ml-3 text-slate-600">Loading your consents...</span>
          </div>
        ) : items.length === 0 ? (
          <div className="py-8 text-center">
            <FileText className="h-10 w-10 text-slate-400 mx-auto mb-3" />
            <p className="text-slate-700 font-medium mb-1">No consent records yet</p>
            <p className="text-sm text-slate-500 mb-4">
              Principal ID: <code className="text-xs bg-slate-100 px-2 py-0.5 rounded">{principalId}</code>
            </p>
            <p className="text-xs text-slate-400">
              Records will appear here once you grant a consent through the DPDPA banner or a form.
            </p>
          </div>
        ) : (
          <div className="space-y-4 mt-2">
            <div className="text-xs text-slate-500 pb-2 border-b border-slate-100">
              Principal ID:{' '}
              <code className="bg-slate-100 px-2 py-0.5 rounded">{principalId}</code>{' '}
              · {items.length} record{items.length !== 1 ? 's' : ''}
            </div>

            {items.map((c) => {
              const v = statusVariant(c.status);
              const StatusIcon = v.icon;
              const isBusy = actionOn === c.id;
              return (
                <div
                  key={c.id}
                  data-testid={`consent-item-${c.id}`}
                  className="border border-slate-200 rounded-xl p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-sm font-semibold text-slate-900">
                          {c.purpose_name || 'Consent'}
                        </span>
                        <Badge className={`${v.bg} border`}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {v.label}
                        </Badge>
                        {c.template_version && (
                          <span className="text-xs text-slate-500">
                            v{c.template_version}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 font-mono truncate">
                        ID: {c.id}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-xs text-slate-600 mb-4">
                    <div className="flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5 text-slate-400" />
                      <span className="text-slate-500">Granted:</span>
                      <span className="font-medium text-slate-700">{fmt(c.granted_at)}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5 text-slate-400" />
                      <span className="text-slate-500">Expires:</span>
                      <span className="font-medium text-slate-700">{fmt(c.expires_at)}</span>
                    </div>
                    {c.withdrawn_at && (
                      <div className="col-span-2 flex items-center gap-1.5 text-red-600">
                        <XCircle className="h-3.5 w-3.5" />
                        <span>Withdrawn: {fmt(c.withdrawn_at)}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 pt-2 border-t border-slate-100">
                    {c.status === 'active' && (
                      <Button
                        data-testid={`withdraw-${c.id}`}
                        size="sm"
                        variant="outline"
                        disabled={isBusy}
                        onClick={() => handleWithdraw(c.id)}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                      >
                        {isBusy ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                        ) : (
                          <XCircle className="h-3.5 w-3.5 mr-1.5" />
                        )}
                        Withdraw
                      </Button>
                    )}
                    {(c.status === 'withdrawn' || c.status === 'expired') && (
                      <Button
                        data-testid={`renew-${c.id}`}
                        size="sm"
                        variant="outline"
                        disabled={isBusy}
                        onClick={() => handleRenew(c.id)}
                        className="text-cyan-700 border-cyan-200 hover:bg-cyan-50"
                      >
                        {isBusy ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                        ) : (
                          <RotateCw className="h-3.5 w-3.5 mr-1.5" />
                        )}
                        Renew
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter className="mt-4 pt-4 border-t border-slate-100">
          <p className="text-xs text-slate-400 flex-1 text-left">
            All actions are tamper-evident and SHA-256 chained. Powered by dpdpcrm.com.
          </p>
          <Button
            data-testid="consent-manager-close"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConsentManager;
